import React from 'react';

import { LoadingAnimation } from '@sg/garnish';

import { NotFoundView } from '../../../../components';
import { useDeliveryOrderInFlight } from '../../../../hooks';
import { useCustomizationDeliveryRestaurantQuery } from '../../GraphQL/CustomizationDeliveryRestaurant.generated';
import { Customization } from './Customization';

// ─────────────────────────────────────────────────────────────────────────────

export const DeliveryCustomization = (props: Props) => {
  const {
    addressId,
    productSlug,
    lineItemId,
    reorderLineItemId,
    sharedLineItemId,
    sharedLineItemSlug,
    shouldShowUnavailableIngredientsModal,
    isEditProductMode,
    onAddLineItemToCart,
    onEditLineItemInCart,
    onRemoveLineItemFromCart,
  } = props;

  // ─── Queries ─────────────────────────────────────────────────────

  const [{ fetching, data }] = useCustomizationDeliveryRestaurantQuery({
    variables: { input: { addressId } },
    pause: !addressId,
  });

  // ─── Delivery Order In Flight ──────────────────────────────────────

  useDeliveryOrderInFlight({ pause: !addressId }); // results later used from the cache.

  // ─────────────────────────────────────────────────────────────────────

  if (fetching) {
    return <LoadingAnimation />;
  }

  if (data?.locationByAddress.__typename !== 'Location') {
    return <NotFoundView />;
  }

  // ─── Data ────────────────────────────────────────────────────────

  const { locationByAddress } = data;
  const { restaurant, vendor } = locationByAddress;
  const { deliveryFee, slug: restaurantSlug } = restaurant;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Customization
      restaurantSlug={restaurantSlug}
      productSlug={productSlug}
      addressId={addressId}
      deliveryFee={deliveryFee}
      vendor={vendor}
      reorderLineItemId={reorderLineItemId}
      lineItemId={lineItemId}
      sharedLineItemId={sharedLineItemId}
      sharedLineItemSlug={sharedLineItemSlug}
      shouldShowUnavailableIngredientsModal={shouldShowUnavailableIngredientsModal}
      isEditProductMode={isEditProductMode}
      onAddLineItemToCart={onAddLineItemToCart}
      onEditLineItemInCart={onEditLineItemInCart}
      onRemoveLineItemFromCart={onRemoveLineItemFromCart}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  addressId: string;
  productSlug: string;
  lineItemId?: string;
  reorderLineItemId?: string;
  sharedLineItemId?: string;
  sharedLineItemSlug?: string;
  shouldShowUnavailableIngredientsModal?: boolean;
  isEditProductMode: boolean;
  onAddLineItemToCart?: () => void;
  onEditLineItemInCart?: () => void;
  onRemoveLineItemFromCart?: () => void;
};
